import type { CookieSetOptions } from 'universal-cookie';
import Cookies from 'universal-cookie';
import { CONFIG } from '@frameio/config';
import { isTest } from '../config/helpers';
import { decodeBase64, encodeBase64 } from '../encode';
import { X_UPLOAD_AUTHORIZATION_HEADER } from './constants';
import { encodeHeaderStringBase64 } from './encodeHeaderStringBase64';

export { encodeHeaderStringBase64, X_UPLOAD_AUTHORIZATION_HEADER };

// Mirrors the equivalent workaround in `shareAuth`: a path-scoped cookie can
// fail to be exposed to JS when the page is loaded via a redirect. The backup
// covers the current session; the cookie reads fine on reload.
const LOCAL_PASSPHRASE_BACKUP: { [uploadLinkId: string]: string } = {};

/**
 * Store the passphrase for an upload link in a cookie so a visitor who has
 * already entered it isn't asked again. This is also where the apollo
 * `uploadAuthLink` reads the passphrase from.
 *
 * @param uploadLinkId id of the upload link being visited
 * @param passphrase passphrase entered by the visitor
 */
export const setPassphraseById = (
  uploadLinkId: string,
  passphrase: string
): void => {
  const cookies = new Cookies(uploadLinkId);
  const cookieParams = getCookieParams(uploadLinkId);
  const encodedPassphrase = encodeBase64(passphrase);

  cookies.set(uploadLinkId, encodedPassphrase, cookieParams);
  LOCAL_PASSPHRASE_BACKUP[uploadLinkId] = encodedPassphrase;
};

/**
 * Get the passphrase for an upload link.
 *
 * @param id id of the upload link being visited
 * @returns the stored passphrase, if any
 */
export const getPassphraseById = (id: string): string | undefined => {
  const cookies = new Cookies(id);
  const uploadPassphrase = cookies.get(id) || LOCAL_PASSPHRASE_BACKUP[id];
  return decodeBase64(uploadPassphrase);
};

/**
 * Cookie settings for upload-link passphrase cookies. Scoped to `/upload/:id`
 * so the passphrase only loads for the matching link.
 *
 * @environments client
 */
const getCookieParams = (uploadLinkId: string): CookieSetOptions => {
  // Don't scope cookies in tests, because then they can't be read.
  if (isTest()) return {};

  return {
    domain: CONFIG.cookieDomain,
    expires: undefined, // expire at the end of the session
    path: `/upload/${uploadLinkId}`,
    sameSite: 'lax',
    secure: true, // only set over HTTPS
  };
};

/**
 * Build the upload-auth headers for a server-side request. Mirrors
 * `getShareAuthHeaders`. Uploads run with the link creator's permissions, so
 * there is no identified-user concept here.
 */
export const getUploadAuthHeaders = (
  uploadLinkId?: string,
  { encodedPassphrase }: { encodedPassphrase?: string } = {}
) => {
  if (!uploadLinkId) return {};
  return {
    [X_UPLOAD_AUTHORIZATION_HEADER]: encodeHeaderStringBase64(
      uploadLinkId,
      encodedPassphrase ? decodeBase64(encodedPassphrase) : undefined
    ),
  };
};
